/**
 * The calculator → app handoff (ON-6: "nothing is retyped").
 *
 * When someone emails themselves a result, the API returns a `save_token` that
 * identifies the row it just wrote. Until now the calculator threw that token
 * away, which meant a person who did the free check and then created an account
 * had to answer all seven steps again — the exact retyping ON-6 exists to
 * prevent. This module is the small piece of shared ground that closes it.
 *
 * WHY A PACKAGE FOR FORTY LINES. Two separately-bundled apps read and write one
 * browser-local value. A key spelled differently in each is not a compile error,
 * not a test failure, and not visible in review — it is a silent "we could not
 * find your check" for every user, forever. Sharing the code removes the
 * possibility rather than testing for it.
 *
 * WHY localStorage AND NOT A URL. The token is a read capability for a saved
 * result: possession of it links that result to an account. ADR-0027 already
 * refused to put the *unsubscribe* token anywhere a referrer, proxy log or
 * shoulder could catch it, and this one is strictly more sensitive — it reaches
 * income figures rather than a mailing preference. Same-origin browser storage
 * keeps it on the device of the person who created it and out of every URL.
 *
 * WHAT THIS IS NOT. It is not a session, not an identity, and not a fallback
 * for signing in. It is a one-time hint that survives the walk from `/check/`
 * to an account, and `clear()` is called the moment it has been used.
 *
 * TWO WAYS TO CARRY A CHECK ACROSS, and they are for different journeys.
 *
 *   `token` — the save token from an emailed copy. Server-side, durable, and
 *   the only option once the result has left the browser.
 *
 *   `answers` — the wizard answers themselves. No server round trip, no email
 *   address, nothing transmitted: someone who finishes the check and presses
 *   "start" has not agreed to send us anything yet, and §9.2 says nothing
 *   leaves the browser until they choose it. This is the path F1 uses.
 *
 * The answers are safe to hold here because of what the wizard already refuses
 * to collect: which disability or carer's benefit somebody receives is reduced
 * to a plain boolean before it ever leaves the question (ADR-0023), so there is
 * no special-category data in this shape to leak. `answers-parity.test.ts`
 * asserts that, so a future field cannot quietly change it.
 */

/** Namespaced so it cannot collide with anything else the origin stores. */
const KEY = "verity.handoff.v1";

/**
 * Three years, and DELIBERATELY SHORTER than the record it points at.
 *
 * This was derived from retention: results were kept three years (ADR-0026), so
 * a token older than that pointed at a deleted row and could only produce a
 * confusing "not found". ADR-0047 moved retention to seven years from the
 * conclusion of the matter, which removes that reasoning without changing the
 * number.
 *
 * It stays at three because retention is a CEILING here, not a target. This is
 * a capability sitting in localStorage on a device we do not control, and
 * "expires before the row does" is the only property that matters. Following
 * retention upward would leave a working token in a browser for seven years to
 * save a retype.
 */
const MAX_AGE_DAYS = 365 * 3;

export interface Handoff {
  /** The `save_token` returned by POST /api/saveResult, when there is one. */
  token?: string;
  /**
   * The wizard answers, for someone who never emailed themselves a copy. An
   * opaque record here on purpose: this package must not depend on the
   * calculator's types, and the app validates the shape when it replays them.
   */
  answers?: Record<string, unknown>;
  /** ISO date the check was taken. Used only to expire the hint. */
  saved_at: string;
  /** Which stage the check was for — lets the app greet with something specific. */
  stage?: string;
}

/**
 * Every function here is total: storage can be disabled, full, or cleared
 * between writing and reading, and a handoff that fails must cost the user a
 * retype rather than an error page. Nothing in this module ever throws.
 */
function storage(): Storage | null {
  try {
    // Safari in private mode has historically had localStorage present but
    // throwing on write, so presence is not enough to go on.
    const s = globalThis.localStorage;
    const probe = `${KEY}.probe`;
    s.setItem(probe, "1");
    s.removeItem(probe);
    return s;
  } catch {
    return null;
  }
}

export function remember(handoff: Handoff): void {
  const s = storage();
  if (!s) return;
  try {
    s.setItem(KEY, JSON.stringify(handoff));
  } catch {
    // Quota, or a browser that refuses. The check still worked; the user will
    // simply be asked for their answers again.
  }
}

export function recall(now: Date): Handoff | null {
  const s = storage();
  if (!s) return null;
  let raw: string | null;
  try {
    raw = s.getItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Someone else's data, or ours from a shape we no longer understand.
    clear();
    return null;
  }

  const value = parsed as Partial<Handoff>;
  if (typeof value.saved_at !== "string") {
    clear();
    return null;
  }

  // A token, if present, must be the shape the API accepts: its own schema
  // requires 20–128 characters, and anything else did not come from us.
  const hasToken =
    typeof value.token === "string" && value.token.length >= 20 && value.token.length <= 128;
  const hasAnswers =
    typeof value.answers === "object" && value.answers !== null && !Array.isArray(value.answers);

  // One or the other, or there is nothing to carry.
  if (!hasToken && !hasAnswers) {
    clear();
    return null;
  }

  const savedAt = Date.parse(value.saved_at);
  if (Number.isNaN(savedAt) || now.getTime() - savedAt > MAX_AGE_DAYS * 86_400_000) {
    clear();
    return null;
  }

  return {
    ...(hasToken ? { token: value.token as string } : {}),
    ...(hasAnswers ? { answers: value.answers as Record<string, unknown> } : {}),
    saved_at: value.saved_at,
    ...(typeof value.stage === "string" ? { stage: value.stage } : {}),
  };
}

/** Call as soon as the token has been used. A hint that has been spent is litter. */
export function clear(): void {
  const s = storage();
  if (!s) return;
  try {
    s.removeItem(KEY);
  } catch {
    /* nothing worth doing */
  }
}
